// src/components/Header.tsx
import React, { useRef, useLayoutEffect, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase/config';
import { useTripContext } from '../contexts/TripContext';
import { FaRegUserCircle } from "react-icons/fa";
import { MdMenu, MdClose } from 'react-icons/md';
import logo from '../images/logo.png';

const Header: React.FC = () => {
  const { user } = useAuth();
  const { resetTripContext } = useTripContext();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      // Store original styles
      const originalStyle = window.getComputedStyle(document.body).overflow;
      const originalPosition = window.getComputedStyle(document.body).position;
      
      // Apply multiple scroll lock techniques
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100%';
      document.documentElement.style.overflow = 'hidden';
      
      // Also add CSS class as backup
      document.body.classList.add('modal-open');
      document.documentElement.classList.add('modal-open');
      
      // Store original values for cleanup
      document.body.setAttribute('data-original-overflow', originalStyle);
      document.body.setAttribute('data-original-position', originalPosition);
    } else {
      // Restore original styles
      const originalOverflow = document.body.getAttribute('data-original-overflow') || 'unset';
      const originalPosition = document.body.getAttribute('data-original-position') || 'static';
      
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.width = '';
      document.body.style.height = '';
      document.documentElement.style.overflow = '';
      
      // Remove CSS classes
      document.body.classList.remove('modal-open');
      document.documentElement.classList.remove('modal-open');
      
      // Clean up attributes
      document.body.removeAttribute('data-original-overflow');
      document.body.removeAttribute('data-original-position');
    }

    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.position = 'static';
      document.body.style.width = '';
      document.body.style.height = '';
      document.documentElement.style.overflow = '';
      document.body.classList.remove('modal-open');
      document.documentElement.classList.remove('modal-open');
    };
  }, [mobileOpen]);
  const location = useLocation();

  const handleLogout = async () => {
    try {
      if (auth) {
        await signOut(auth);
        setDropdownOpen(false);
        setMobileOpen(false);
        navigate("/");
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleHomeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    resetTripContext();
    setMobileOpen(false);
    navigate('/');
  };

  const handleYourTripsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setMobileOpen(false);
    setDropdownOpen(false);
    
    if (user) {
      // User is logged in, navigate to account page
      navigate('/account');
    } else {
      // User is not logged in, navigate to login page
      navigate('/login');
    }
  };

  useLayoutEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isHomepage = location.pathname === '/';

  return (
    <header
      className={`main-nav-header relative flex items-center justify-center px-4 md:px-10 border-b border-gray-200 transition-colors z-50 ${isHomepage ? 'bg-blue-100 border-none' : 'bg-white'}`}
      style={{ height: '60px' }}
    >
      {/* Left Section: Logo */}
      <div className="absolute left-4 md:left-10 flex items-center h-full overflow-visible">
        <div style={{ width: '60px', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: 0, padding: 0, overflow: 'hidden'}}>
          <a href="/" onClick={handleHomeClick} style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img
              src={logo}
              alt="Logo"
              style={{
                width: '30px',
                height: '30px',
                maxWidth: '30px',
                maxHeight: '30px',
                objectFit: 'contain',
                objectPosition: 'center',
                flexShrink: 0
              }}
            />
          </a>
        </div>
      </div>

      {/* Center Section: Navigation (desktop) */}
      <div className="hidden md:flex justify-center w-full h-full">
        <nav className="flex justify-between items-center gap-7 w-[35%] px-10 h-full">
          <a href="/" onClick={handleHomeClick} className="text-gray-700 font-bold font-buenos-aires hover:text-primary transition-colors">
            Home
          </a>
          <Link to="/trip-overview" className="text-gray-700 font-bold font-buenos-aires hover:text-primary transition-colors">
            Travel Planner
          </Link>
          <Link to="/explore" className="text-gray-700 font-bold font-buenos-aires hover:text-primary transition-colors">
            Explore Trips
          </Link>
          <a href="/account" onClick={handleYourTripsClick} className="text-gray-700 font-bold font-buenos-aires hover:text-primary transition-colors">
            Your Trips
          </a>
        </nav>
      </div>

      {/* Right Section: User + Mobile Menu Button */}
      <div className="absolute right-4 md:right-10 flex items-center h-full gap-3">
        <button
          className="md:hidden p-2 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          onClick={() => setMobileOpen(prev => !prev)}
          aria-label="Toggle menu"
          aria-expanded={mobileOpen}
          aria-controls="mobile-menu"
        >
          {mobileOpen ? <MdClose size={22} className="text-gray-700"/> : <MdMenu size={22} className="text-gray-700"/>}
        </button>
        <div className="relative hidden md:block" ref={dropdownRef}>
          <FaRegUserCircle 
            className="text-2xl text-gray-700 cursor-pointer"
            onClick={() => setDropdownOpen(!dropdownOpen)}
            aria-haspopup="true"
            aria-expanded={dropdownOpen}
            aria-label="User Menu"
          />
          {dropdownOpen && (
            <div className="absolute top-full right-0 bg-white border border-gray-200 rounded shadow-lg min-w-[140px] z-[999999999] flex flex-col">
              {user ? (
                <>
                  <Link 
                    to="/account" 
                    onClick={() => setDropdownOpen(false)}
                    className="p-2.5 text-gray-700 hover:bg-gray-100 text-sm font-buenos-aires"
                  >
                    Account
                  </Link>
                  <button 
                    onClick={handleLogout}
                    className="p-2.5 text-gray-700 hover:bg-gray-100 text-left text-sm font-buenos-aires"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link 
                  to="/login" 
                  onClick={() => setDropdownOpen(false)}
                  className="p-2.5 text-gray-700 hover:bg-gray-100 text-sm font-buenos-aires"
                >
                  Login
                </Link>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Mobile Slide-over Menu */}
      {mobileOpen && (
        <div className="md:hidden">
          <div 
            className="fixed inset-0 bg-black/40 z-[9998]"
            onClick={() => setMobileOpen(false)}
          />
          <div
            id="mobile-menu"
            className="fixed top-0 right-0 h-full w-64 bg-white z-[9999] shadow-xl p-4 flex flex-col"
            role="dialog"
            aria-modal="true"
          >
            <nav className="mt-12 flex flex-col gap-2">
              <a
                href="/"
                onClick={handleHomeClick}
                className="px-3 py-2 rounded text-gray-800 hover:bg-gray-100 font-buenos-aires"
              >
                Home
              </a>
              <Link
                to="/trip-overview"
                onClick={() => setMobileOpen(false)}
                className="px-3 py-2 rounded text-gray-800 hover:bg-gray-100 font-buenos-aires"
              >
                Travel Planner
              </Link>
              <Link
                to="/explore"
                onClick={() => setMobileOpen(false)}
                className="px-3 py-2 rounded text-gray-800 hover:bg-gray-100 font-buenos-aires"
              >
                Explore Trips
              </Link>
              <a
                href="/account"
                onClick={handleYourTripsClick}
                className="px-3 py-2 rounded text-gray-800 hover:bg-gray-100 font-buenos-aires"
              >
                Your Trips
              </a>

              {/* Account section in mobile menu */}
              <div className="mt-3 border-t border-gray-200 pt-3">
                {user ? (
                  <>
                    <Link
                      to="/account"
                      onClick={() => setMobileOpen(false)}
                      className="block px-3 py-2 rounded text-gray-800 hover:bg-gray-100 font-buenos-aires"
                    >
                      Account
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-3 py-2 rounded text-gray-800 hover:bg-gray-100 font-buenos-aires"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <Link
                    to="/login"
                    onClick={() => setMobileOpen(false)}
                    className="block px-3 py-2 rounded text-gray-800 hover:bg-gray-100 font-buenos-aires"
                  >
                    Login
                  </Link>
                )}
              </div>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;